import { Avatar, AvatarFallback, Popover, PopoverContent, PopoverTrigger } from '@sea-snag/ui';
import { Crown } from 'lucide-react';

import type { AlertRuleUserSummary } from '../../lib/api-client';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

interface AlertPeopleCellProps {
  createdBy: AlertRuleUserSummary;
  subscribers: AlertRuleUserSummary[];
}

export function AlertPeopleCell({ createdBy, subscribers }: AlertPeopleCellProps) {
  const others = subscribers.filter((s) => s.pid !== createdBy.pid);
  const visible = [createdBy, ...others.slice(0, 2)];
  const overflow = Math.max(0, others.length - 2);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center -space-x-2 hover:opacity-80"
          aria-label="Show subscribers"
        >
          {visible.map((u) => {
            const isCreator = u.pid === createdBy.pid;
            return (
              // Creator sits on top so the crown isn't covered by the next
              // overlapping avatar in the -space-x-2 stack.
              <span key={u.pid} className={`relative ${isCreator ? 'z-10' : ''}`}>
                <Avatar className="size-7 border-2 border-background">
                  <AvatarFallback className="text-xs">{initials(u.name)}</AvatarFallback>
                </Avatar>
                {isCreator && (
                  <Crown
                    className="absolute -top-2 left-1/2 size-3 -translate-x-1/2 text-amber-500 drop-shadow"
                    aria-label="Creator"
                  />
                )}
              </span>
            );
          })}
          {overflow > 0 && (
            <span className="ml-2 inline-flex size-7 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium text-muted-foreground">
              +{overflow}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <ul className="divide-y">
          {[createdBy, ...others].map((u) => (
            <li key={u.pid} className="flex items-center gap-3 p-3">
              <span className="relative">
                <Avatar className="size-8">
                  <AvatarFallback className="text-xs">{initials(u.name)}</AvatarFallback>
                </Avatar>
                {u.pid === createdBy.pid && (
                  <Crown className="absolute -top-2 left-1/2 size-3 -translate-x-1/2 text-amber-500" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{u.name}</p>
                <p className="truncate text-xs text-muted-foreground">{u.email}</p>
              </div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
